import { Head } from '@inertiajs/react';
import { useIntl } from 'react-intl';
import { FOCUS_RING } from '@/Components/Marketing/focusRing';
import Footer from '@/Components/Marketing/Footer';
import Header from '@/Components/Marketing/Header';
// The hero grid ships with the layout (Task 10b-3): the eight page heroes
// and the error page draw it, and as a module of its own it was a chunk of
// its own on each. The other pages carry its few bytes without a request for it.
import '@/Components/Marketing/Texture';
import useFocusOnVisit from '@/Components/Marketing/useFocusOnVisit';
import { cn } from '@/lib/utils';

/**
 * The shell of every marketing page, all of them light: the floating header,
 * the page itself (which ends on its own last section) and the footer.
 * `title` goes to the document title; `cta` is the header's call to action
 * (Header: a demo unless the page books a consultation);
 * `footerNewsletter={false}` drops the footer's sign-up on a page that has
 * its own; `translated` keeps every language in the footer's switcher, on a
 * page translated into them (Footer). The page leaves its rings' offset to
 * its sections (the default, white, passes on the canvas).
 */
export default function MarketingLayout({ title, cta, canLogin, footerNewsletter = true, translated = false, children }) {
    const { formatMessage } = useIntl();
    // After an Inertia visit, <main> takes the focus (audit 40).
    useFocusOnVisit('main');

    const skipToContent = event => {
        // Focus rather than follow the fragment: scrollToAnchor would smooth-scroll
        // the (very tall) <main> to the middle of the viewport.
        event.preventDefault();
        document.getElementById('main')?.focus();
    };

    return (
        <>
            {title != null && <Head title={title} />}
            <div className="relative flex min-h-[100dvh] flex-col bg-mk-canvas font-sans text-mk-ink antialiased">
                <a
                    href="#main"
                    onClick={skipToContent}
                    className={cn(
                        // not-sr-only resets padding, so the padding has to be a focus: class too.
                        'sr-only z-50 rounded-lg text-sm font-medium focus:not-sr-only focus:fixed focus:left-4 focus:top-24 focus:px-4 focus:py-2',
                        FOCUS_RING,
                        'bg-mk-surface text-mk-ink shadow-mk-soft',
                    )}
                >
                    {formatMessage({ id: 'site.nav.skip' })}
                </a>
                <Header cta={cta} canLogin={canLogin} />
                {/* #main: app.css also reads it, to reveal a focused field clear of the header. */}
                <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
                    {children}
                </main>
                <Footer newsletter={footerNewsletter} translated={translated} />
            </div>
        </>
    );
}
